<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed, watch } from "vue";
import { auth, db } from "@/firebase";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  onSnapshot,
} from "firebase/firestore";
import TutorHeader from "@/components/TutorHeader.vue";
import { useProfilePhoto } from "@/composables/useProfilePhoto";
import { useRealtimeProfile } from "@/composables/useRealtimeProfile";
import { PencilSquareIcon, AcademicCapIcon } from "@heroicons/vue/24/outline";
import { ShieldCheckIcon } from "@heroicons/vue/24/solid";
import type { EducationItem, CertificationItem } from "@/types/profile";
// Updated EducationItem with fieldOfStudy field
import EditBioModal from "@/components/Student-Tutor/Profile/EditBioModal.vue";
import EditTextFieldModal from "@/components/Student-Tutor/Profile/EditTextFieldModal.vue";
import EditSubjectsModal from "@/components/Student-Tutor/Profile/EditSubjectsModal.vue";
import EditEducationModal from "@/components/Student-Tutor/Profile/EditEducationModal.vue";
import EditCertificationsModal from "@/components/Student-Tutor/Profile/EditCertificationsModal.vue";
import EditLanguagesModal from "@/components/Student-Tutor/Profile/EditLanguagesModal.vue";
import UploadPhotoModal from "@/components/Student-Tutor/Profile/UploadPhotoModal.vue";
import UserAvatar from "@/components/UserAvatar.vue";
import GoogleCalendarConnect from "@/components/GoogleCalendarConnect.vue";
import { logDebug, logInfo, logError, logSuccess } from "@/utils/secureLogger";
import { logProfileChange } from "@/services/auditLogService";
import FileReport from "@/components/FileReport.vue";

const profileImage = ref<string | null>(null);
const userData = ref<any>(null);
const isLoadingProfile = ref(false);
const sessionCount = ref(0);
const averageRating = ref(0);

// Modal states
const isBioModalOpen = ref(false);
const isSubjectsModalOpen = ref(false);
const isPhotoModalOpen = ref(false);
const isPhoneModalOpen = ref(false);
const isAddressModalOpen = ref(false);
const isLinkedInModalOpen = ref(false);
const isWebsiteModalOpen = ref(false);
const isSaving = ref(false);
const isEducationModalOpen = ref(false);
const isCertificationsModalOpen = ref(false);
const isLanguagesModalOpen = ref(false);
const isTutorTitleModalOpen = ref(false);
const isExperienceModalOpen = ref(false);

// Available subjects for tutors (same as students)
const availableSubjects = [
  "Oral Communication",
  "Reading and Writing",
  "Komunikasyon at Pananaliksik sa Wika at Kulturang Pilipino",
  "Pagbasa at Pagsusuri ng Iba't-Ibang Teksto Tungo sa Pananaliksik",
  "21st Century Literature from the Philippines and the World",
  "General Mathematics",
  "Statistics and Probability",
  "Earth and Life Science",
  "Physical Science",
  "Earth Science",
];

// Profile photo composable
const { isUploading, uploadError, uploadSuccess, selectAndUploadPhoto } = useProfilePhoto();

// Computed property to check if profile image exists
const hasProfileImage = computed(() => !!profileImage.value);

// Computed properties for user data
const fullName = computed(() => {
  if (!userData.value) return "Loading...";
  const parts = [
    userData.value.firstName,
    userData.value.middleName,
    userData.value.lastName,
    userData.value.suffix,
  ].filter(Boolean);
  return parts.join(" ") || "Tutor";
});

const tutorTitle = computed(() => {
  if (!userData.value) return "";
  return userData.value.tutorTitle || "Professional Tutor";
});

const experienceYears = computed(() => {
  if (!userData.value?.experienceYears) return "";
  const years = userData.value.experienceYears;
  return `${years}+ year${years !== 1 ? "s" : ""} experience`;
});

// Contact Information
const userEmail = computed(() => userData.value?.email || auth.currentUser?.email || "");
const userPhone = computed(() => userData.value?.phoneNumber || "Not provided");
const userAddress = computed(() => userData.value?.address || "Not provided");

// Social Links
const userLinkedIn = computed(() => userData.value?.linkedInProfile || "Not provided");
const userWebsite = computed(() => userData.value?.personalWebsite || "Not provided");

// About Me
const userBio = computed(() => userData.value?.bio || "No bio yet.");

// Calendar Integration Status
const calendarIntegrated = computed(() => Boolean(userData.value?.googleCalendarIntegrated));

const userSubjects = computed(() => {
  if (!userData.value?.subjects) return [];
  const subjects = userData.value.subjects;
  if (Array.isArray(subjects)) return subjects;
  if (typeof subjects === "string")
    return subjects
      .split(",")
      .map((s: string) => s.trim())
      .filter(Boolean);
  return [];
});

const userEducation = computed((): EducationItem[] => {
  if (!userData.value?.education) return [];

  // Format education details with proper tense based on graduation year
  return userData.value.education.map((edu: EducationItem) => {
    let details = edu.details;

    // Extract year from details or use graduationYear field
    let year = userData.value?.graduationYear;

    if (details) {
      // Check if details contains "Graduated YYYY" or just "YYYY"
      const yearMatch = details.match(/\d{4}/);
      if (yearMatch) {
        year = parseInt(yearMatch[0]);
      }
    }

    // Format with proper tense if we have a year
    if (year) {
      const currentYear = new Date().getFullYear();

      if (year >= currentYear) {
        details = `Expected graduation ${year}`;
      } else {
        details = `Graduated ${year}`;
      }
    }

    return {
      ...edu,
      details,
    };
  });
});

const userCertifications = computed((): CertificationItem[] => {
  if (!userData.value?.certifications) return [];
  return userData.value.certifications;
});

const userLanguages = computed(() => {
  if (!userData.value?.languages) return [];
  const languages = userData.value.languages;
  if (Array.isArray(languages)) return languages;
  if (typeof languages === "string")
    return languages
      .split(",")
      .map((l: string) => l.trim())
      .filter(Boolean);
  return [];
});

// Additional computed properties for registration fields
const userInstitution = computed(() => userData.value?.institution || "Not provided");
const userFieldOfStudy = computed(() => userData.value?.fieldOfStudy || "Not provided");
const userEducationLevel = computed(() => {
  const level = userData.value?.educationLevel;
  return level ? level.trim() : "Not provided";
});

const memberSince = computed(() => {
  if (!userData.value?.createdAt) return "Recently";
  try {
    const date = userData.value.createdAt.toDate
      ? userData.value.createdAt.toDate()
      : new Date(userData.value.createdAt);
    return date.toLocaleDateString("en-US", { month: "numeric", day: "numeric", year: "numeric" });
  } catch {
    return "Recently";
  }
});

const lastActive = computed(() => {
  if (!userData.value?.lastActive) return "Recently";
  try {
    const date = userData.value.lastActive.toDate
      ? userData.value.lastActive.toDate()
      : new Date(userData.value.lastActive);
    return date.toLocaleDateString("en-US", { month: "numeric", day: "numeric", year: "numeric" });
  } catch {
    return "Recently";
  }
});

// Load session statistics
const loadSessionStats = async (userId: string) => {
  try {
    // Count completed sessions
    const sessionsQuery = query(
      collection(db, "sessions"),
      where("tutorId", "==", userId),
      where("sessionStatus", "==", "completed")
    );
    const sessionsSnapshot = await getDocs(sessionsQuery);
    sessionCount.value = sessionsSnapshot.size;

    // ✅ Read pre-calculated average rating from user document (INSTANT!)
    const userDoc = await getDoc(doc(db, "users", userId));
    if (userDoc.exists()) {
      const userData = userDoc.data();
      averageRating.value = userData?.averageRating || 0;

      logInfo(
        `Loaded ${sessionCount.value} completed sessions and pre-calculated rating (${averageRating.value})`
      );
    } else {
      averageRating.value = 0;
    }
  } catch (error) {
    logError("Error loading session stats", error);
  }
};

// Load user profile data with real-time sync
const loadUserProfile = async () => {
  try {
    isLoadingProfile.value = true;
    logDebug("Loading tutor profile data");

    // Wait for auth to be ready
    const user = await new Promise<any>((resolve) => {
      if (auth.currentUser) {
        resolve(auth.currentUser);
      } else {
        const unsubscribe = auth.onAuthStateChanged((user) => {
          unsubscribe();
          resolve(user);
        });
      }
    });

    if (!user) {
      logDebug("No authenticated user found");
      isLoadingProfile.value = false;
      return;
    }

    logInfo("Setting up real-time listener for user:", { uid: user.uid });

    // Set up real-time listener for immediate and ongoing updates
    const userRef = doc(db, "users", user.uid);
    const unsubscribe = onSnapshot(
      userRef,
      async (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          userData.value = data;
          profileImage.value = data.profileImage || data.profilePhotoUrl || data.photoURL || null;

          logSuccess("Tutor profile synced from Firebase", {
            fields: Object.keys(data),
            hasPhoto: !!profileImage.value,
            bio: data.bio ? "present" : "missing",
            certifications: data.certifications?.length || 0,
          });

          // Load session stats
          await loadSessionStats(user.uid);
        } else {
          logError("Tutor profile document does not exist in Firebase");
        }
        isLoadingProfile.value = false;
      },
      (error) => {
        logError("Error in real-time profile listener", error);
        isLoadingProfile.value = false;
      }
    );

    // Clean up listener on component unmount
    onUnmounted(() => {
      unsubscribe();
      logDebug("Cleaned up tutor profile real-time listener");
    });
  } catch (error) {
    logError("Error loading tutor profile", error);
    isLoadingProfile.value = false;
  }
};

// Handle bio save
const handleBioSave = async (newBio: string) => {
  try {
    isSaving.value = true;
    const user = auth.currentUser;
    if (!user) {
      logError("No authenticated user found when updating bio");
      return;
    }

    const userRef = doc(db, "users", user.uid);
    const oldBio = userData.value?.bio || "";

    await updateDoc(userRef, {
      bio: newBio,
      updatedAt: new Date(),
    });

    // Create audit log for bio change
    await logProfileChange(user.uid, "bio", oldBio, newBio, "tutor");

    if (userData.value) {
      userData.value.bio = newBio;
    }

    logSuccess("Bio updated successfully");
    isSubjectsModalOpen.value = false;
    isBioModalOpen.value = false;
  } catch (error) {
    logError("Error updating bio", error);
  } finally {
    isSaving.value = false;
  }
};

// Handle subjects save
const handleSubjectsSave = async (newSubjects: string[]) => {
  try {
    isSaving.value = true;
    const user = auth.currentUser;
    if (!user) {
      logError("No authenticated user found when updating subjects");
      return;
    }

    const userRef = doc(db, "users", user.uid);
    await updateDoc(userRef, {
      subjects: newSubjects,
      updatedAt: new Date(),
    });

    if (userData.value) {
      userData.value.subjects = newSubjects;
    }

    logSuccess("Subjects updated successfully");
    isSubjectsModalOpen.value = false;
  } catch (error) {
    logError("Error updating subjects", error);
  } finally {
    isSaving.value = false;
  }
};

// Handle profile photo upload
const handlePhotoUpload = async () => {
  try {
    const result = await selectAndUploadPhoto();

    if (result.success && result.url) {
      profileImage.value = result.url;
      logSuccess("Profile photo uploaded successfully");

      // Show success notification
      setTimeout(() => {
        uploadSuccess.value = false;
      }, 3000);
    }
  } catch (error) {
    logError("Error uploading profile photo", error);
  }
};

// Handle photo upload from modal
const handlePhotoUploadFromModal = async (file: File) => {
  try {
    const { uploadProfilePhoto } = useProfilePhoto();
    const result = await uploadProfilePhoto(file);

    if (result.success && result.url) {
      profileImage.value = result.url;
      isPhotoModalOpen.value = false;
      logSuccess("Profile photo uploaded successfully from modal");

      // Show success notification
      setTimeout(() => {
        uploadSuccess.value = false;
      }, 3000);
    }
  } catch (error) {
    logError("Error uploading profile photo from modal", error);
  }
};

// Phone validation function
const validatePhone = (phone: string): string | null => {
  if (!phone) {
    return "Phone number is required";
  }

  // Check if phone number contains any letters
  const hasLetters = /[a-zA-Z]/.test(phone);
  if (hasLetters) {
    return "Phone number cannot contain letters. Please enter numbers only.";
  }

  // Remove all non-digit characters to count actual digits
  const digitsOnly = phone.replace(/\D/g, "");

  // Check if it has exactly 11 digits
  if (digitsOnly.length !== 11) {
    return "Phone number must contain exactly 11 digits.";
  }

  // Philippine phone number format: must start with 09
  if (!digitsOnly.startsWith("09")) {
    return "Phone number must start with 09 (e.g., 09123456789).";
  }

  // Final validation: must be exactly 09xxxxxxxxx format
  const isValid = /^09\d{9}$/.test(digitsOnly);

  if (!isValid) {
    return "Invalid phone number format. Use: 09XXXXXXXXX";
  }

  return null;
};

// URL validation function
const validateURL = (url: string): string | null => {
  if (!url) {
    return "URL is required";
  }

  // Check for common invalid patterns
  if (url.includes("@") || url.endsWith(".") || url.includes("..")) {
    return "Please enter a valid URL (e.g., https://www.example.com)";
  }

  // Check if it's a valid URL with protocol
  try {
    const urlObj = new URL(url);
    // Additional check for valid domain
    if (!urlObj.hostname.includes(".")) {
      return "Please enter a valid URL with a complete domain (e.g., https://www.example.com)";
    }
    return null;
  } catch {
    // If not a valid URL, check if it's a LinkedIn profile format
    if (url.includes("linkedin.com/in/")) {
      return null; // Accept LinkedIn profiles without protocol
    }
    return "Please enter a valid URL (e.g., https://www.example.com)";
  }
};

// Handle phone save
const handlePhoneSave = async (newPhone: string) => {
  try {
    isSaving.value = true;
    const user = auth.currentUser;
    if (!user) {
      logError("No authenticated user found when updating phone");
      return;
    }

    const userRef = doc(db, "users", user.uid);
    const oldPhone = userData.value?.phoneNumber || "";

    await updateDoc(userRef, {
      phoneNumber: newPhone,
      updatedAt: new Date(),
    });

    // Create audit log for phone number change
    await logProfileChange(user.uid, "phoneNumber", oldPhone, newPhone, "tutor");

    if (userData.value) {
      userData.value.phoneNumber = newPhone;
    }

    logSuccess("Phone number updated successfully");
    isPhoneModalOpen.value = false;
  } catch (error) {
    logError("Error updating phone number", error);
  } finally {
    isSaving.value = false;
  }
};

// Handle address save
const handleAddressSave = async (newAddress: string) => {
  try {
    isSaving.value = true;
    const user = auth.currentUser;
    if (!user) {
      logError("No authenticated user found when updating address");
      return;
    }

    const userRef = doc(db, "users", user.uid);
    const oldAddress = userData.value?.address || "";

    await updateDoc(userRef, {
      address: newAddress,
      updatedAt: new Date(),
    });

    // Create audit log for address change
    await logProfileChange(user.uid, "address", oldAddress, newAddress, "tutor");

    if (userData.value) {
      userData.value.address = newAddress;
    }

    logSuccess("Address updated successfully");
    isAddressModalOpen.value = false;
  } catch (error) {
    logError("Error updating address", error);
  } finally {
    isSaving.value = false;
  }
};

// Handle LinkedIn save
const handleLinkedInSave = async (newLinkedIn: string) => {
  try {
    isSaving.value = true;
    const user = auth.currentUser;
    if (!user) {
      logError("No authenticated user found when updating LinkedIn");
      return;
    }

    const userRef = doc(db, "users", user.uid);
    const oldLinkedIn = userData.value?.linkedInProfile || "";

    await updateDoc(userRef, {
      linkedInProfile: newLinkedIn,
      updatedAt: new Date(),
    });

    // Create audit log for LinkedIn profile change
    await logProfileChange(user.uid, "linkedInProfile", oldLinkedIn, newLinkedIn, "tutor");

    if (userData.value) {
      userData.value.linkedInProfile = newLinkedIn;
    }

    logSuccess("LinkedIn profile updated successfully");
    isLinkedInModalOpen.value = false;
  } catch (error) {
    logError("Error updating LinkedIn profile", error);
  } finally {
    isSaving.value = false;
  }
};

// Handle website save
const handleWebsiteSave = async (newWebsite: string) => {
  try {
    isSaving.value = true;
    const user = auth.currentUser;
    if (!user) {
      logError("No authenticated user found when updating website");
      return;
    }

    const userRef = doc(db, "users", user.uid);
    const oldWebsite = userData.value?.personalWebsite || "";

    await updateDoc(userRef, {
      personalWebsite: newWebsite,
      updatedAt: new Date(),
    });

    // Create audit log for personal website change
    await logProfileChange(user.uid, "personalWebsite", oldWebsite, newWebsite, "tutor");

    if (userData.value) {
      userData.value.personalWebsite = newWebsite;
    }

    logSuccess("Personal website updated successfully");
    isWebsiteModalOpen.value = false;
  } catch (error) {
    logError("Error updating personal website", error);
  } finally {
    isSaving.value = false;
  }
};

// Handle education save
const handleEducationSave = async (items: EducationItem[]) => {
  try {
    isSaving.value = true;
    const user = auth.currentUser;
    if (!user) {
      logError("No authenticated user found when updating education");
      return;
    }

    const userRef = doc(db, "users", user.uid);
    const oldEducation = userData.value?.education || [];

    await updateDoc(userRef, {
      education: items,
      updatedAt: new Date(),
    });

    // Create audit log for education change
    await logProfileChange(
      user.uid,
      "education",
      JSON.stringify(oldEducation),
      JSON.stringify(items),
      "tutor"
    );

    if (userData.value) {
      userData.value.education = items;
    }

    logSuccess("Education updated successfully");
    isEducationModalOpen.value = false;
  } catch (error) {
    logError("Error updating education", error);
  } finally {
    isSaving.value = false;
  }
};

// Handle certifications save
const handleCertificationsSave = async (items: CertificationItem[]) => {
  try {
    isSaving.value = true;
    const user = auth.currentUser;
    if (!user) {
      logError("No authenticated user found when updating certifications");
      return;
    }

    const userRef = doc(db, "users", user.uid);
    const oldCertifications = userData.value?.certifications || [];

    await updateDoc(userRef, {
      certifications: items,
      updatedAt: new Date(),
    });

    // Create audit log for certifications change
    await logProfileChange(
      user.uid,
      "certifications",
      JSON.stringify(oldCertifications),
      JSON.stringify(items),
      "tutor"
    );

    if (userData.value) {
      userData.value.certifications = items;
    }

    logSuccess("Certifications updated successfully");
    isCertificationsModalOpen.value = false;
  } catch (error) {
    logError("Error updating certifications", error);
  } finally {
    isSaving.value = false;
  }
};

// Handle languages save
const handleLanguagesSave = async (newItems: string[]) => {
  try {
    isSaving.value = true;
    const user = auth.currentUser;
    if (!user) {
      logError("No authenticated user found when updating languages");
      return;
    }

    const userRef = doc(db, "users", user.uid);
    await updateDoc(userRef, {
      languages: newItems,
      updatedAt: new Date(),
    });

    if (userData.value) {
      userData.value.languages = newItems;
    }

    logSuccess("Languages updated successfully");
    isLanguagesModalOpen.value = false;
  } catch (error) {
    logError("Error updating languages", error);
  } finally {
    isSaving.value = false;
  }
};

// Handle tutor title save
const handleTutorTitleSave = async (newTitle: string) => {
  try {
    isSaving.value = true;
    const user = auth.currentUser;
    if (!user) {
      logError("No authenticated user found when updating tutor title");
      return;
    }

    const userRef = doc(db, "users", user.uid);
    await updateDoc(userRef, {
      tutorTitle: newTitle,
      updatedAt: new Date(),
    });

    if (userData.value) {
      userData.value.tutorTitle = newTitle;
    }

    logSuccess("Tutor title updated successfully");
    isTutorTitleModalOpen.value = false;
  } catch (error) {
    logError("Error updating tutor title", error);
  } finally {
    isSaving.value = false;
  }
};

// Handle experience years save
const handleExperienceSave = async (newExperience: string) => {
  try {
    isSaving.value = true;
    const user = auth.currentUser;
    if (!user) {
      logError("No authenticated user found when updating experience");
      return;
    }

    // Convert to number
    const experienceYears = parseInt(newExperience, 10);
    if (isNaN(experienceYears) || experienceYears < 0) {
      logError("Invalid experience years value");
      return;
    }

    const userRef = doc(db, "users", user.uid);
    await updateDoc(userRef, {
      experienceYears: experienceYears,
      updatedAt: new Date(),
    });

    if (userData.value) {
      userData.value.experienceYears = experienceYears;
    }

    logSuccess("Experience years updated successfully");
    isExperienceModalOpen.value = false;
  } catch (error) {
    logError("Error updating experience years", error);
  } finally {
    isSaving.value = false;
  }
};

// Validate experience years
const validateExperience = (value: string): string | null => {
  const num = parseInt(value, 10);
  if (isNaN(num)) {
    return "Please enter a valid number";
  }
  if (num < 0) {
    return "Years of experience cannot be negative";
  }
  if (num > 100) {
    return "Please enter a reasonable number of years";
  }
  return null;
};

// Validate graduation year
const validateGraduationYear = (value: string): string | null => {
  const num = parseInt(value, 10);
  if (isNaN(num)) {
    return "Please enter a valid year";
  }
  if (num < 1950) {
    return "Graduation year must be 1950 or later";
  }
  if (num > 2025) {
    return "Graduation year must be 2025 or earlier";
  }
  return null;
};

onMounted(async () => {
  document.title = "ElevatED – Tutor Profile";

  // Load profile with real-time sync
  await loadUserProfile();
});
</script>

<template>
  <FileReport />
  <div class="tutor-page-wrapper">
    <TutorHeader />

    <!-- Main Content -->
    <div class="max-w-5xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 pt-24 sm:pt-32 lg:pt-44 pb-24">
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6 max-w-full">
        <!-- Left Column - Profile and Contact -->
        <div class="space-y-4 sm:space-y-6 lg:sticky lg:top-40 lg:self-start min-w-0">
          <!-- Profile Card -->
          <div
            class="bg-white rounded-lg sm:rounded-xl shadow-[3px_3px_0_0_rgba(0,0,0,1)] sm:shadow-[6px_4px_0_0_rgba(0,0,0,1)] border-2 border-black p-4 sm:p-6"
          >
            <div class="text-center">
              <!-- Profile Avatar -->
              <div class="relative inline-block mb-3 sm:mb-4">
                <UserAvatar :src="profileImage" :name="fullName" size="xl" />
                <!-- Loading Overlay -->
                <div
                  v-if="isUploading"
                  class="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center"
                >
                  <svg
                    class="animate-spin h-6 w-6 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    ></circle>
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                </div>
                <!-- Edit Button -->
                <button
                  @click="isPhotoModalOpen = true"
                  :disabled="isUploading"
                  class="absolute bottom-0 right-0 w-6 h-6 sm:w-8 sm:h-8 bg-orange-500 rounded-full flex items-center justify-center border-2 border-white hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Upload profile photo"
                >
                  <svg
                    v-if="!isUploading"
                    class="w-3 h-3 sm:w-4 sm:h-4 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                  <svg
                    v-else
                    class="animate-spin h-3 w-3 sm:h-4 sm:w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    ></circle>
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                </button>
              </div>

              <!-- Name and Info -->
              <h2 class="text-xl sm:text-2xl font-bold text-gray-900 mb-1">{{ fullName }}</h2>

              <!-- Tutor Title -->
              <div class="flex items-center justify-center gap-2 mb-1">
                <p v-if="tutorTitle" class="text-gray-600 text-sm sm:text-base">{{ tutorTitle }}</p>
                <p v-else class="text-gray-400 italic text-sm sm:text-base">No title set</p>
              </div>

              <!-- Experience Years -->
              <div class="flex items-center justify-center gap-2 mb-3 sm:mb-4">
                <p v-if="experienceYears" class="text-gray-500 text-xs sm:text-sm">
                  {{ experienceYears }}
                </p>
                <p v-else class="text-gray-400 italic text-xs sm:text-sm">No experience set</p>
              </div>

              <!-- Separator Line -->
              <div class="w-full h-px bg-gray-200 mb-3 sm:mb-4"></div>

              <!-- Stats -->
              <div class="flex justify-center space-x-6 sm:space-x-8">
                <div class="text-center">
                  <div class="text-2xl sm:text-3xl font-bold text-blue-600">{{ sessionCount }}</div>
                  <div class="text-xs sm:text-sm text-gray-600">Sessions</div>
                </div>
                <div class="text-center">
                  <div class="text-2xl sm:text-3xl font-bold text-orange-500">
                    {{ averageRating ? averageRating.toFixed(1) : "-" }}
                  </div>
                  <div class="text-xs sm:text-sm text-gray-600">Rating</div>
                </div>
              </div>

              <!-- End of Profile Card Content -->
            </div>
          </div>

          <!-- Contact Information Card -->
          <div
            class="bg-white rounded-lg shadow-[6px_4px_0_0_rgba(0,0,0,1)] border-2 border-black p-4 sm:p-6"
          >
            <div class="flex items-center mb-3 sm:mb-4">
              <svg
                class="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 mr-2"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 1V3H9V1L3 7V9H21ZM12 8C13.1 8 14 8.9 14 10V22H10V10C10 8.9 10.9 8 12 8Z"
                />
              </svg>
              <h3 class="text-base sm:text-lg font-semibold text-gray-900">Contact Information</h3>
            </div>

            <div class="space-y-2 sm:space-y-3">
              <div v-if="userEmail" class="flex items-center text-gray-600">
                <svg
                  class="w-3 h-3 sm:w-4 sm:h-4 mr-2 sm:mr-3 text-gray-400 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M20,8L12,13L4,8V6L12,11L20,6M20,4H4C2.89,4 2,4.89 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V6C22,5.11 21.1,4 20,4Z"
                  />
                </svg>
                <span class="text-xs sm:text-sm break-all">{{ userEmail }}</span>
              </div>
              <div class="flex items-center justify-between text-gray-600 gap-2">
                <div class="flex items-center flex-1 min-w-0">
                  <svg
                    class="w-3 h-3 sm:w-4 sm:h-4 mr-2 sm:mr-3 text-gray-400 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M6.62,10.79C8.06,13.62 10.38,15.94 13.21,17.38L15.41,15.18C15.69,14.9 16.08,14.82 16.43,14.93C17.55,15.3 18.75,15.5 20,15.5A1,1 0 0,1 21,16.5V20A1,1 0 0,1 20,21A17,17 0 0,1 3,4A1,1 0 0,1 4,3H7.5A1,1 0 0,1 8.5,4C8.5,5.25 8.7,6.45 9.07,7.57C9.18,7.92 9.1,8.31 8.82,8.59L6.62,10.79Z"
                    />
                  </svg>
                  <span class="text-xs sm:text-sm truncate" :title="userPhone">{{
                    userPhone
                  }}</span>
                </div>
                <button
                  @click="isPhoneModalOpen = true"
                  class="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex-shrink-0"
                  title="Edit phone number"
                >
                  <PencilSquareIcon class="w-4 h-4" />
                </button>
              </div>
              <div class="flex items-center justify-between text-gray-600 gap-2">
                <div class="flex items-center flex-1 min-w-0">
                  <svg
                    class="w-3 h-3 sm:w-4 sm:h-4 mr-2 sm:mr-3 text-gray-400 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M12,11.5A2.5,2.5 0 0,1 9.5,9A2.5,2.5 0 0,1 12,6.5A2.5,2.5 0 0,1 14.5,9A2.5,2.5 0 0,1 12,11.5M12,2A7,7 0 0,0 5,9C5,14.25 12,22 12,22C12,22 19,14.25 19,9A7,7 0 0,0 12,2Z"
                    />
                  </svg>
                  <span class="text-xs sm:text-sm truncate" :title="userAddress">{{
                    userAddress
                  }}</span>
                </div>
                <button
                  @click="isAddressModalOpen = true"
                  class="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex-shrink-0"
                  title="Edit address"
                >
                  <PencilSquareIcon class="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          <!-- Social Links Card -->
          <div
            class="bg-white rounded-lg shadow-[6px_4px_0_0_rgba(0,0,0,1)] border-2 border-black p-4 sm:p-6"
          >
            <div class="flex items-center mb-3 sm:mb-4">
              <svg
                class="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 mr-2"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM8.287 5.906c-.778.324-2.334.994-4.666 2.01-.378.15-.577.298-.595.442-.03.243.275.339.69.47l.175.055c.408.133.958.288 1.243.294.26.006.549-.1.868-.32 2.179-1.471 3.304-2.214 3.374-2.23.05-.012.12-.026.166.016.047.041.042.12.037.141-.03.129-1.227 1.241-1.846 1.817-.193.18-.33.307-.358.336a8.154 8.154 0 0 1-.188.186c-.38.366-.664.64.015 1.088.327.216.589.393.85.571.284.194.568.387.936.629.093.06.183.125.27.187.331.236.63.448.997.414.214-.02.435-.22.547-.82.265-1.417.786-4.486.906-5.751a1.426 1.426 0 0 0-.013-.315.337.337 0 0 0-.114-.217.526.526 0 0 0-.31-.093c-.3.005-.763.166-2.984 1.09z"
                />
              </svg>
              <h3 class="text-base sm:text-lg font-semibold text-gray-900">Social Links</h3>
            </div>

            <div class="space-y-2 sm:space-y-3">
              <div class="flex items-center justify-between text-gray-600 gap-2">
                <div class="flex items-center flex-1 min-w-0">
                  <svg
                    class="w-3 h-3 sm:w-4 sm:h-4 mr-2 sm:mr-3 text-blue-600 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"
                    />
                  </svg>
                  <span class="text-xs sm:text-sm truncate" :title="userLinkedIn">{{
                    userLinkedIn
                  }}</span>
                </div>
                <button
                  @click="isLinkedInModalOpen = true"
                  class="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex-shrink-0"
                  title="Edit LinkedIn profile"
                >
                  <PencilSquareIcon class="w-4 h-4" />
                </button>
              </div>
              <div class="flex items-center justify-between text-gray-600 gap-2">
                <div class="flex items-center flex-1 min-w-0">
                  <svg
                    class="w-3 h-3 sm:w-4 sm:h-4 mr-2 sm:mr-3 text-green-600 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.024-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.888 2.741.097.118.112.223.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.402.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.357-.629-2.748-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24.009 12.017 24c6.624 0 11.99-5.367 11.99-11.987C24.007 5.367 18.641.001.012.001z"
                    />
                  </svg>
                  <span class="text-xs sm:text-sm truncate" :title="userWebsite">{{
                    userWebsite
                  }}</span>
                </div>
                <button
                  @click="isWebsiteModalOpen = true"
                  class="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex-shrink-0"
                  title="Edit personal website"
                >
                  <PencilSquareIcon class="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Column - About Me, Professional Info, Availability -->
        <div class="lg:col-span-2">
          <div class="space-y-4 sm:space-y-6">
            <!-- Profile Content -->
            <div class="space-y-4 sm:space-y-6">
              <!-- About Me Card -->
              <div
                class="bg-white rounded-lg shadow-[6px_4px_0_0_rgba(0,0,0,1)] border-2 border-black p-6"
              >
                <div class="flex items-center justify-between mb-4">
                  <div class="flex items-center">
                    <svg class="w-5 h-5 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 24 24">
                      <path
                        d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4M11,16.5L18,9.5L16.59,8.09L11,13.67L7.91,10.59L6.5,12L11,16.5Z"
                      />
                    </svg>
                    <h3 class="text-lg font-semibold text-gray-900">About Me</h3>
                  </div>
                  <button
                    @click="isBioModalOpen = true"
                    class="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Edit bio"
                  >
                    <PencilSquareIcon class="w-5 h-5" />
                  </button>
                </div>
                <p class="text-gray-600 leading-relaxed text-sm">
                  {{ userBio }}
                </p>
                <div class="mt-4 text-xs text-gray-500">
                  <span>Member since {{ memberSince }} • Last active {{ lastActive }}</span>
                </div>
              </div>

              <!-- Professional Information Card -->
              <div
                class="bg-white rounded-lg shadow-[6px_4px_0_0_rgba(0,0,0,1)] border-2 border-black p-6"
              >
                <div class="flex items-center mb-4">
                  <svg class="w-5 h-5 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path
                      d="M12,3L1,9L12,15L21,10.09V17H23V9M5,13.18V17.18L12,21L19,17.18V13.18L12,17L5,13.18Z"
                    />
                  </svg>
                  <h3 class="text-lg font-semibold text-gray-900">Professional Information</h3>
                </div>

                <div class="space-y-4">
                  <!-- Education -->
                  <div>
                    <div class="flex items-center justify-between mb-2">
                      <h4 class="text-xs font-medium text-gray-500">Education</h4>
                    </div>
                    <div v-if="userEducation.length > 0" class="space-y-2">
                      <div
                        v-for="(edu, index) in userEducation"
                        :key="index"
                        class="flex items-start"
                      >
                        <AcademicCapIcon class="w-5 h-5 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                        <div class="text-sm text-gray-900">
                          <div v-if="edu.fieldOfStudy" class="font-semibold">
                            {{ edu.fieldOfStudy }}
                          </div>
                          <div v-else class="font-semibold">{{ edu.degree }}</div>
                          <div class="text-gray-600 text-xs">{{ edu.school }}</div>
                          <div v-if="edu.details" class="text-gray-500 text-xs">
                            {{ edu.details }}
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Display individual education fields if education array is empty -->
                    <div
                      v-else-if="
                        userEducationLevel !== 'Not provided' ||
                        userInstitution !== 'Not provided' ||
                        userFieldOfStudy !== 'Not provided'
                      "
                      class="space-y-2"
                    >
                      <div class="flex items-start">
                        <AcademicCapIcon class="w-5 h-5 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                        <div class="text-sm text-gray-900">
                          <div v-if="userFieldOfStudy !== 'Not provided'" class="font-semibold">
                            {{ userFieldOfStudy }}
                          </div>
                          <div
                            v-else-if="userEducationLevel !== 'Not provided'"
                            class="font-semibold"
                          >
                            {{ userEducationLevel }}
                          </div>
                          <div
                            v-if="userInstitution !== 'Not provided'"
                            class="text-gray-600 text-xs"
                          >
                            {{ userInstitution }}
                          </div>
                        </div>
                      </div>
                    </div>

                    <p v-else class="text-sm text-gray-500 italic">No education specified</p>
                  </div>

                  <!-- Subjects -->
                  <div>
                    <div class="flex items-center justify-between mb-2">
                      <h4 class="text-xs font-medium text-gray-500">Subjects</h4>
                    </div>
                    <div v-if="userSubjects.length > 0" class="flex flex-wrap gap-2">
                      <span
                        v-for="(subject, index) in userSubjects"
                        :key="index"
                        class="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                      >
                        {{ subject }}
                      </span>
                    </div>
                    <p v-else class="text-sm text-gray-500 italic">No subjects specified</p>
                  </div>

                  <div>
                    <div class="flex items-center justify-between mb-2">
                      <h4 class="text-xs font-medium text-gray-500">Languages</h4>
                      <button
                        @click="isLanguagesModalOpen = true"
                        class="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit languages"
                      >
                        <PencilSquareIcon class="w-4 h-4" />
                      </button>
                    </div>
                    <div v-if="userLanguages.length > 0" class="flex flex-wrap gap-2">
                      <span
                        v-for="(language, index) in userLanguages"
                        :key="index"
                        class="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full"
                      >
                        {{ language }}
                      </span>
                    </div>
                    <p v-else class="text-sm text-gray-500 italic">No languages specified</p>
                  </div>
                </div>
              </div>

              <!-- Google Calendar Integration Card -->
              <div
                class="bg-white rounded-lg shadow-[6px_4px_0_0_rgba(0,0,0,1)] border-2 border-black p-6"
              >
                <div class="flex items-center mb-4">
                  <svg
                    class="w-5 h-5 text-blue-600 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <h3 class="text-lg font-semibold text-gray-900">Calendar Integration</h3>
                </div>
                <GoogleCalendarConnect />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Success Notification -->
    <Transition name="slide-up">
      <div
        v-if="uploadSuccess"
        class="fixed bottom-6 right-6 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center space-x-3 z-50"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M5 13l4 4L19 7"
          />
        </svg>
        <span class="font-medium">Profile photo updated successfully!</span>
      </div>
    </Transition>

    <!-- Error Notification -->
    <Transition name="slide-up">
      <div
        v-if="uploadError"
        class="fixed bottom-6 right-6 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center space-x-3 z-50"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
        <span class="font-medium">{{ uploadError }}</span>
      </div>
    </Transition>

    <!-- Modals -->
    <EditBioModal
      :is-open="isBioModalOpen"
      :current-bio="userBio"
      @close="isBioModalOpen = false"
      @save="handleBioSave"
      :is-saving="isSaving"
    />

    <UploadPhotoModal
      :is-open="isPhotoModalOpen"
      @close="isPhotoModalOpen = false"
      @upload="handlePhotoUploadFromModal"
    />

    <!-- Phone Number Edit Modal -->
    <EditTextFieldModal
      :is-open="isPhoneModalOpen"
      title="Edit Phone Number"
      label="Phone Number"
      :current-value="userPhone"
      @close="isPhoneModalOpen = false"
      @save="handlePhoneSave"
      :validate-fn="validatePhone"
      placeholder="Enter your phone number (e.g. 09123456789)"
      input-type="phone"
      :max-length="11"
      :is-saving="isSaving"
    />

    <!-- Address Edit Modal -->
    <EditTextFieldModal
      :is-open="isAddressModalOpen"
      title="Edit Address"
      label="Address"
      :current-value="userAddress"
      @close="isAddressModalOpen = false"
      @save="handleAddressSave"
      placeholder="Enter your complete address"
      :is-saving="isSaving"
    />

    <!-- LinkedIn Profile Edit Modal -->
    <EditTextFieldModal
      :is-open="isLinkedInModalOpen"
      title="Edit LinkedIn Profile"
      label="LinkedIn URL"
      :current-value="userLinkedIn"
      @close="isLinkedInModalOpen = false"
      @save="handleLinkedInSave"
      :validate-fn="validateURL"
      placeholder="Enter your LinkedIn profile URL"
      :is-saving="isSaving"
    />

    <!-- Personal Website Edit Modal -->
    <EditTextFieldModal
      :is-open="isWebsiteModalOpen"
      title="Edit Personal Website"
      label="Website URL"
      :current-value="userWebsite"
      @close="isWebsiteModalOpen = false"
      @save="handleWebsiteSave"
      :validate-fn="validateURL"
      :is-saving="isSaving"
      placeholder="Enter your personal website URL"
    />

    <!-- Subjects Edit Modal -->
    <EditSubjectsModal
      :is-open="isSubjectsModalOpen"
      :current-subjects="userSubjects"
      :available-subjects="availableSubjects"
      @close="isSubjectsModalOpen = false"
      @save="handleSubjectsSave"
      :is-saving="isSaving"
    />

    <!-- Education Edit Modal -->
    <EditEducationModal
      :is-open="isEducationModalOpen"
      :items="userEducation"
      :institution="userData?.institution"
      :field-of-study="userData?.fieldOfStudy"
      :education-level="userData?.educationLevel"
      :graduation-year="userData?.graduationYear"
      @close="isEducationModalOpen = false"
      @save="handleEducationSave"
    />

    <!-- Certifications Edit Modal -->
    <EditCertificationsModal
      :is-open="isCertificationsModalOpen"
      :items="userCertifications"
      @close="isCertificationsModalOpen = false"
      @save="handleCertificationsSave"
    />

    <!-- Languages Edit Modal -->
    <EditLanguagesModal
      :is-open="isLanguagesModalOpen"
      :items="userLanguages"
      @close="isLanguagesModalOpen = false"
      @save="handleLanguagesSave"
      :is-saving="isSaving"
    />

    <!-- Tutor Title Edit Modal -->
    <EditTextFieldModal
      :is-open="isTutorTitleModalOpen"
      title="Edit Tutor Title"
      label="Tutor Title"
      :current-value="tutorTitle"
      @close="isTutorTitleModalOpen = false"
      @save="handleTutorTitleSave"
      placeholder="e.g., Professional Math Tutor, English Language Specialist"
      :is-saving="isSaving"
    />

    <!-- Experience Years Edit Modal -->
    <EditTextFieldModal
      :is-open="isExperienceModalOpen"
      title="Edit Years of Experience"
      label="Years of Experience"
      :current-value="userData?.experienceYears?.toString() || '0'"
      @close="isExperienceModalOpen = false"
      @save="handleExperienceSave"
      :validate-fn="validateExperience"
      placeholder="Enter number of years (e.g., 5)"
      :is-saving="isSaving"
    />
  </div>
</template>

<style scoped>
.tutor-page-wrapper {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: url("/tutor-bg.png") center top/100% auto no-repeat;
  background-attachment: fixed;
  position: relative;
  width: 100%;
  max-width: 100vw;
  overflow-x: hidden;
}

@media (max-width: 768px) {
  .tutor-page-wrapper {
    background-size: cover;
    background-position: center center;
  }
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter-from {
  transform: translateY(100px);
  opacity: 0;
}

.slide-up-leave-to {
  transform: translateY(100px);
  opacity: 0;
}
</style>
